import React, { useState } from 'react';
import styles from './ItemImage.module.css';

interface ItemImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  containerClassName?: string;
  fallbackText?: string;
  aspectRatio?: string; // e.g., "4/3", "16/9", "1/1"
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  width?: number;
  height?: number;
}

const ItemImage: React.FC<ItemImageProps> = ({
  src,
  alt,
  className = '',
  containerClassName = '',
  fallbackText = 'No image available',
  aspectRatio = '4/3',
  loading = 'lazy',
  fetchPriority = 'auto',
  decoding = 'async',
  width,
  height,
}) => {
  const [isLoading, setIsLoading] = useState(!!src);
  const [hasError, setHasError] = useState(false);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div
      className={`w-full max-w-md bg-base-200 rounded-xl p-6 ${containerClassName}`}
    >
      {src && !hasError ? (
        <div className={styles.imageContainer} style={{ aspectRatio }}>
          {/* Loading skeleton */}
          {isLoading && (
            <div className={`${styles.skeleton} bg-base-300 rounded-lg`}>
              <div className="flex items-center justify-center h-full">
                <span className="text-neutral/75 text-sm">Loading...</span>
              </div>
            </div>
          )}

          {/* Actual image */}
          <img
            src={src}
            alt={alt}
            className={`${styles.itemImage} ${className} ${isLoading ? styles.imageLoading : styles.imageLoaded}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading={loading}
            fetchPriority={fetchPriority}
            decoding={decoding}
            width={width}
            height={height}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </div>
      ) : (
        <div
          className={`${styles.imageContainer} bg-base-300 rounded-lg flex items-center justify-center`}
          style={{ aspectRatio }}
        >
          <span className="text-neutral/75">{fallbackText}</span>
        </div>
      )}
    </div>
  );
};

export default ItemImage;
